import type { ApiResponse } from "@entrilia/shared/api/types";
import { logError } from "@entrilia/shared/logging";
import type { ReportHistoryItem } from "../../../../../../api/types/svTypes";
import { selectCurrentReportId } from "../selectors/currentReportSelectors";
import {
  selectVersionHistoryBackupConfiguration,
  selectVersionHistoryPreviewData,
} from "../selectors/versionHistorySelectors";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk";
import { createCurrentReportAction, createUiSettingsAction, createVersionHistoryAction } from "../utils/storeUtils";
import { applyPreviewConfiguration } from "./currentReportThunks";
import { baselineEditingHistory } from "./editingHistoryThunks";

type GetVersionHistoryFunction = (reportId: string, signal?: AbortSignal) => Promise<ApiResponse<ReportHistoryItem[]>>;

export const getVersionHistory = createTypedAsyncThunk<ReportHistoryItem[], GetVersionHistoryFunction>(
  "store/getVersionHistory",
  async (getVersionHistoryFunction, { getState }) => {
    try {
      const state = getState();
      const reportId = selectCurrentReportId(state);
      if (!reportId) {
        logError(new Error("Report ID not found"), "getVersionHistory");
        return [];
      }
      const response = await getVersionHistoryFunction(reportId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error("Failed to get version history");
    } catch (error) {
      logError(error, "getVersionHistory");
      throw new Error("Failed to get version history", { cause: error });
    }
  }
);

export const revertToBackupConfiguration = createTypedAsyncThunk(
  "store/revertToBackupConfiguration",
  async (_, { getState, dispatch }) => {
    const state = getState();
    const backupConfiguration = selectVersionHistoryBackupConfiguration(state);
    const previewData = selectVersionHistoryPreviewData(state);
    if (backupConfiguration && previewData) {
      await dispatch(
        applyPreviewConfiguration({
          datasetId: backupConfiguration.dataSetId,
          configuration: backupConfiguration.currentConfiguration,
          reportType: backupConfiguration.currentReportType,
        })
      );
      dispatch(createCurrentReportAction("updateCurrentReportType", backupConfiguration.currentReportType));
      dispatch(baselineEditingHistory());
    }
    dispatch(createVersionHistoryAction("updatePreviewConfiguration", undefined));
    dispatch(createVersionHistoryAction("updateBackupConfiguration", undefined));
  }
);

export const keepPreviewConfiguration = createTypedAsyncThunk(
  "store/keepPreviewConfiguration",
  async (_, { dispatch }) => {
    dispatch(createVersionHistoryAction("updatePreviewConfiguration", undefined));
    dispatch(createVersionHistoryAction("updateBackupConfiguration", undefined));
  }
);

export const clearVersionHistory = createTypedAsyncThunk("store/clearVersionHistory", async (_, { dispatch }) => {
  dispatch(createUiSettingsAction("setVersionHistoryOpen", false));
  dispatch(createVersionHistoryAction("updatePreviewConfiguration", undefined));
  dispatch(createVersionHistoryAction("updateBackupConfiguration", undefined));
  dispatch(createVersionHistoryAction("updateCurrentConfiguration", undefined));
});
